import React, { createContext, useContext, useEffect, useState } from 'react';
import moment from 'moment';

import { useAuth } from 'contexts/auth';
import courseRequests from 'services/course';
import groupRequests from 'services/group';
import { message } from 'shell/components';
import { TYPES } from 'utils/constants';

const CourseContext = createContext();
const useUser = () => useContext(CourseContext);

const UserProvider = (props) => {
    const { auth, currentUser } = useAuth();

    const [course, setCourse] = useState(null);
    const [courseLoading, setCourseLoading] = useState(false);
    const [courseGroups, setCourseGroups] = useState([]);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [variance, setVariance] = useState();

    const listCourses = async () => {
        setCourseLoading(true);
        const courseData = await courseRequests.findCourse(auth.course_id);
        setCourse(courseData);
        setCourseLoading(false);
    };

    const getCourseGroups = async () => {
        if (!auth || !auth?.course_id) return setCourseGroups([]);
        const currentCourseGroups = await courseRequests.findCourseGroups(auth.course_id);
        setCourseGroups(currentCourseGroups);
    };

    const getCurrentGroup = async (group_id) => {
        const currentGroup = await groupRequests.findGroup(group_id);
        setCurrentGroup(currentGroup);
        return currentGroup;
    };

    const getVariance = async () => {
        const varianceRes = await courseRequests.findCourseVariances(auth.course_id);
        setVariance(varianceRes);
        return varianceRes;
    };

    const getGroupUsers = async (group_id) => {
        if (!auth) return [];
        const currentGroupUsers = await groupRequests.findGroupUsers(group_id || auth.group_id);
        return currentGroupUsers;
    };

    const getCourseUsers = async () => {
        if (!auth) return [];
        const getCourseUsersReq = await courseRequests.findCourseUsers(auth.course_id);
        return getCourseUsersReq;
    };

    const getGroupAccounts = async (group_id) => {
        if (!auth) return [];
        const currentGroupAccounts = await groupRequests.findGroupAccounts(group_id || auth.group_id);
        return currentGroupAccounts;
    };

    const getCourseAccounts = async () => {
        if (!auth) return [];
        const getCourseAccountsReq = await courseRequests.findCourseAccounts(auth.course_id);
        return getCourseAccountsReq;
    };

    const listContribution = async (group_id) => {
        if (!auth) return {};
        const contribution = await groupRequests.findGroupContribution(group_id || auth.group_id);
        return contribution;
    };

    const listDistribution = async (group_id) => {
        if (!auth) return;
        const distribution = await groupRequests.findGroupDistribution(group_id || auth.group_id);
        return distribution;
    };

    const updateTeamName = async (name) => {
        const prevGroupName = currentGroup.name;
        setCurrentGroup((currentGroup) => ({
            ...currentGroup,
            name,
        }));
        try {
            await groupRequests.updateGroup(currentGroup.id, { name });
            message.success('Team name successfully updated.');
        } catch (e) {
            setCurrentGroup((currentGroup) => ({
                ...currentGroup,
                name: prevGroupName,
            }));
            message.error('Could not update team name.');
        }
    };

    const getUserNameWithFallback = () => {
        if (currentUser.display_name && currentUser.last_name) {
            return `${currentUser.display_name} ${currentUser.last_name}`;
        } else if (currentUser.first_name && currentUser.last_name) {
            return `${currentUser.first_name} ${currentUser.last_name}`;
        } else {
            return currentUser.username;
        }
    };

    const getActivityList = async (mode = 'today', skip, top) => {
        if (auth.role === 'student') {
            const computeTimestamp = (mode) => {
                if (mode !== 'today') {
                    return moment(new Date()).subtract(7, 'day').toDate().getTime();
                } else {
                    const today = moment(new Date());
                    today.hour(0);
                    today.minute(0);
                    today.second(0);
                    today.millisecond(0);
                    return today.toDate().getTime();
                }
            };

            return groupRequests.findGroupActivities(
                auth.group_id,
                JSON.stringify({
                    created_on: {
                        $gte: computeTimestamp(mode),
                    },
                    type: {
                        $in: [TYPES.TASK, TYPES.FEEDBACK, TYPES.FILE, TYPES.SHARED_URL],
                    },
                }),
                'created_on',
                skip,
                top
            );
        }
        return [];
    };

    const findAllCourses = async () => {
        const coursesAndAcounts = await courseRequests.listCourses('admin');
        return coursesAndAcounts;
    };

    useEffect(() => {
        if (auth && auth.course_id) {
            const getInitData = async () => {
                await getCourseGroups();
                if (auth.role === 'student') await getCurrentGroup(auth.group_id);
                await listCourses();
                await getVariance();
            };
            getInitData();
        }
    }, [auth]);

    return (
        <CourseContext.Provider
            {...props}
            value={{
                course,
                courseGroups,
                currentGroup,
                courseLoading,
                setCourse,
                getCourseGroups,
                getCurrentGroup,
                setCurrentGroup,
                getGroupUsers,
                getCourseUsers,
                getGroupAccounts,
                getCourseAccounts,
                listContribution,
                listDistribution,
                variance,
                findAllCourses,
                // getDeviation,
                // getAverage,
                // getDiffSum,
                // getPosition,
                // getProgress,
                updateTeamName,
                getVariance,
                getUserNameWithFallback,
                getActivityList,
            }}
        />
    );
};

export { useUser };
export default UserProvider;
